<template>
  <main class="main home-page">
    <div class="main-wrapper">
      <header-wrapper class="section hero-section">
        <div class="wrapper">
          <div class="content">
            <div class="title big">{{ $t(`Crypto in Your Pocket!`) }}</div>
            <div class="desc">
              {{ $t(`Your Trusted Partner for Crypto-Fiat Exchange`) }}
            </div>
            <div @click="checkAuth" class="button get-started-btn">
              {{ $t(`Get started`) }}
            </div>
          </div>
          <img class="payment-img" src="../assets/bitcoin.png" alt="" />
        </div>
      </header-wrapper>

      <div class="section section-2">
        <div class="wrapper">
          <div class="title">{{ $t(`How it works`) }}</div>
          <div class="step-list">
            <div
              v-for="({ title, desc }, index) in howItWorksSteps"
              :key="index"
              :class="{ odd: !((index + 1) % 2 === 0) }"
              class="step-list__item"
            >
              <div class="step__index">0{{ index + 1 }}</div>
              <div class="step__title">{{ title }}</div>
              <div class="step__desc">{{ desc }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="section section-3">
        <div class="wrapper">
          <div class="columns">
            <div class="column">
              <div class="title">
                {{ $t(`Benefits of using Quantumexchanger`) }}
              </div>
            </div>
            <div class="column">
              <div class="benefits">
                <div
                  v-for="({ icon, title, desc }, index) in benefits"
                  :key="index"
                  :class="{ odd: !((index + 1) % 2 === 0) }"
                  class="benefits__item"
                >
                  <div class="text">
                    <div class="text__title">
                      <div class="icon-wrapper">
                        <img :src="getImageUrl(icon)" alt="" />
                      </div>
                      {{ title }}
                    </div>
                    <div class="text__desc">{{ desc }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="section section-5">
        <div class="wrapper">
          <div class="title big">{{ $t(`Online crypto exchange`) }}</div>
          <div class="card-list">
            <div class="card-list__item well">
              <div class="list" v-if="eurExchangeList.length">
                <div class="column__currency">
                  <div
                    v-for="item in eurExchangeList"
                    :key="item.crypto_currency"
                    class="row"
                  >
                    <div class="desc">
                      <img
                        :src="getImageUrl(`${item.crypto_currency}.svg`)"
                        alt=""
                      />
                    </div>
                    <div class="desc">{{ item.title }}</div>
                    <div class="desc">{{ item.crypto_currency }}</div>
                  </div>
                </div>
                <div class="column__price">
                  <div
                    v-for="item in eurExchangeList"
                    :key="item.crypto_currency"
                    class="row"
                  >
                    € {{ item.price }}
                  </div>
                </div>
                <div class="column__growth">
                  <div
                    v-for="item in eurExchangeList"
                    :key="item.crypto_currency"
                    class="row"
                  >
                    + {{ item.coeff }}%
                  </div>
                </div>
              </div>
            </div>

            <div :class="{ crypto: !fiatToCrypto }" class="card-list__item buy">
              <div class="head">
                <div class="title" v-if="!fiatToCrypto">
                  {{ $t(`Exchange crypto to fiat`) }}
                </div>
                <div class="title" v-if="fiatToCrypto">
                  {{ $t(`Exchange fiat to crypto`) }}
                </div>
                <div class="exchange-container">
                  <ExchangeButton @click="toggleExchangeDirection" />
                </div>
              </div>

              <div class="inputs">
                <select
                  class="currency-select"
                  v-if="eurExchangeList.length"
                  :value="selectedCryptoId"
                  @change="handleCurrencyChange"
                >
                  <option
                    v-for="item in eurExchangeList"
                    :key="item.id"
                    :value="item.id"
                  >
                    {{ item.title }}
                  </option>
                </select>

                <div class="input-container" v-if="fiatToCrypto">
                  <input
                    type="number"
                    :value="giveAmount"
                    @input="handleFiatInput"
                    min="0"
                    step="0.01"
                  />
                  <div class="desc">EUR</div>
                </div>

                <div class="input-container" v-else>
                  <input
                    type="number"
                    :value="takeAmount"
                    @input="handleCryptoInput"
                    min="0"
                    step="0.00000001"
                  />
                  <div class="desc">
                    {{ selectedCrypto?.crypto_currency || "" }}
                  </div>
                </div>
              </div>

              <div class="calculation-breakdown">
                <div class="total">
                  <div class="desc">{{ $t(`You receive:`) }}</div>
                  <div class="desc value">{{ receiveAmountDisplay }}</div>
                </div>
                <div class="total">
                  <div class="desc">
                    {{
                      $t(
                        `Our fee (${(parseFloat(feePercentage) * 100).toFixed(
                          2
                        )}%):`
                      )
                    }}
                  </div>
                  <div class="desc value">{{ feeDisplay }}</div>
                </div>
                <div class="total total--to-pay">
                  <div class="desc">{{ $t(totalToPayLabel) }}</div>
                  <div class="desc value">{{ totalToPayDisplay }}</div>
                </div>
              </div>

              <button
                class="button"
                @click="handleBuyCrypto"
                :disabled="!selectedCrypto || !isValidAmount"
              >
                {{ $t(`Buy Crypto`) }}
              </button>
            </div>
          </div>

          <div class="widget">
            <div id="tradingview-widget"></div>
          </div>
        </div>
      </div>

      <div class="section section-6">
        <div class="wrapper">
          <div class="header__text title">{{ $t(`Contact us`) }}</div>
          <ContactForm />
        </div>
      </div>

      <div class="section section-7">
        <div class="wrapper">
          <div class="top">
            <div class="title big">{{ $t(`FAQ's`) }}</div>
          </div>
          <div class="accordion">
            <div class="item" v-for="(item, index) in $parent.faq" :key="index">
              <div class="faq-question" @click="toggleAnswer(index)">
                <div
                  :class="[
                    'title small left',
                    { active: isActiveAnswer(index) },
                  ]"
                >
                  {{ item.question }}
                </div>
                <img
                  :class="{ 'rotate-icon': isActiveAnswer(index) }"
                  src="./../images/arrow.svg"
                  class="img"
                />
              </div>
              <transition name="fade">
                <div
                  v-html="item.answer"
                  v-if="isActiveAnswer(index)"
                  class="desc faq-answer"
                ></div>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import HeaderWrapper from "@/components/HeaderWrapper.vue";
import ExchangeButton from "@/components/ExchangeButton.vue";
import ContactForm from "@/components/ContactForm.vue";

export default {
  name: "Home",
  props: {
    feePercentage: {
      type: Number,
      default: 0.045,
    },
  },
  components: {
    ContactForm,
    ExchangeButton,
    HeaderWrapper,
  },
  data() {
    return {
      activePanels: [],
      exchangeList: [],
      giveAmount: 100, // For Fiat-to-Crypto, this is the total amount user pays. For Crypto-to-Fiat, this is the amount user receives.
      takeAmount: 0, // For Fiat-to-Crypto, this is the crypto user gets. For Crypto-to-Fiat, this is the crypto user sends.
      selectedCryptoId: null,
      fiatToCrypto: true,
      isLoading: false,
      howItWorksSteps: [
        {
          title: "Sign in",
          desc: "Create an account and complete the verification process. Once your account is verified, you're ready to start trading.",
        },
        {
          title: "Connect your wallet",
          desc: "Select your preferred cryptocurrency wallet from the available options and follow the on-screen instructions to securely sync your wallet.",
        },
        {
          title: "Make a payment",
          desc: "Select the type of transaction, choose the crypto or fiat currency you want to exchange, specify the amount, and review the transaction details carefully. Confirm the transaction.",
        },
      ],
      benefits: [
        {
          title: "Enhanced Security and Reliability",
          desc: "We prioritise the security of your funds and transactions, employing advanced security protocols and multi-level safeguards.",
          icon: "shield.svg",
        },
        {
          title: "Effortless Crypto-Fiat Conversion",
          desc: "Our user-friendly interface and intuitive design make it simple to exchange.",
          icon: "stonks.svg",
        },
        {
          title: "Competitive Rates and Low Fees",
          desc: "We offer competitive rates for crypto-fiat exchanges and maintain transparent fee structures that allow you to make informed decisions.",
          icon: "chart.svg",
        },
        {
          title: "24/7 Customer Support",
          desc: "Our dedicated customer support team is available around the clock to assist you with any questions.",
          icon: "support.svg",
        },
      ],
    };
  },
  computed: {
    formattedFeePercentage() {
      const feePercentage = parseFloat(this.feePercentage);
      if (typeof value === "number" && isFinite(feePercentage)) {
        return feePercentage.toFixed(2);
      }
      return "0.00";
    },
    eurExchangeList() {
      return this.exchangeList.filter((item) => item.currency === "EUR");
    },
    selectedCrypto() {
      return (
        this.eurExchangeList.find(
          (item) => item.id === this.selectedCryptoId
        ) || null
      );
    },
    isValidAmount() {
      return this.fiatToCrypto ? this.giveAmount > 0 : this.takeAmount > 0;
    },
    receiveAmountDisplay() {
      if (this.fiatToCrypto) {
        return this.takeAmount > 0
          ? `${this.takeAmount.toFixed(8)} ${
              this.selectedCrypto.crypto_currency
            }`
          : "...";
      } else {
        return this.giveAmount > 0
          ? `${this.giveAmount.toFixed(2)} EUR`
          : "...";
      }
    },
    feeDisplay() {
      if (this.fiatToCrypto) {
        if (!this.giveAmount) return "...";
        const fee = this.giveAmount * this.feePercentage;
        return `${fee.toFixed(2)} EUR`;
      } else {
        if (!this.takeAmount || !this.selectedCrypto) return "...";
        const feeInCrypto = this.takeAmount * this.feePercentage;
        return `${feeInCrypto.toFixed(8)} ${
          this.selectedCrypto.crypto_currency
        }`;
      }
    },
    totalToPayDisplay() {
      if (this.fiatToCrypto) {
        if (!this.giveAmount) return "...";
        return `${parseFloat(this.giveAmount).toFixed(2)} EUR`;
      } else {
        if (!this.takeAmount || !this.selectedCrypto) return "...";
        return `${parseFloat(this.takeAmount).toFixed(8)} ${
          this.selectedCrypto.crypto_currency
        }`;
      }
    },
    totalToPayLabel() {
      return this.fiatToCrypto ? "Total to pay:" : "Total to send:";
    },
  },
  watch: {
    eurExchangeList: {
      handler(newList) {
        if (newList.length > 0 && !this.selectedCryptoId) {
          this.selectedCryptoId = newList[0].id;
          this.$nextTick(() => {
            this.recalculateAmounts();
          });
        }
      },
      immediate: true,
    },
    selectedCrypto: {
      handler() {
        this.$nextTick(() => {
          this.recalculateAmounts();
        });
      },
    },
  },
  async mounted() {
    if (this.$route.query?.redirect) {
      this.$parent.openSignInModal();
    }

    await this.loadExchangeList();
    this.initializeTradingViewWidget();
  },
  methods: {
    checkAuth() {
      if (this.$parent.isAuth) {
        this.$router.push({ path: "/buy-crypto" });
      } else {
        this.$parent.openSignInModal();
      }
    },
    async loadExchangeList() {
      if (this.isLoading) return;

      this.isLoading = true;
      try {
        const response = await this.$http.get("exchange-top");
        this.exchangeList = response.data.payload || [];
      } catch (error) {
        console.error("Failed to load exchange list:", error);
        this.exchangeList = [];
      } finally {
        this.isLoading = false;
      }
    },
    handleCurrencyChange(event) {
      const newId = parseInt(event.target.value);
      if (newId !== this.selectedCryptoId) {
        this.selectedCryptoId = newId;
      }
    },
    handleFiatInput(event) {
      const value = parseFloat(event.target.value) || 0;
      this.giveAmount = value;
      this.calculateCryptoAmount();
    },
    handleCryptoInput(event) {
      const value = parseFloat(event.target.value) || 0;
      this.takeAmount = value;
      this.calculateFiatAmount();
    },
    calculateCryptoAmount() {
      // Fiat-to-Crypto: giveAmount is total. takeAmount is what user receives.
      if (this.selectedCrypto?.price && this.giveAmount > 0) {
        const amountAfterFee = this.giveAmount * (1 - this.feePercentage);
        this.takeAmount = amountAfterFee / this.selectedCrypto.price;
      } else {
        this.takeAmount = 0;
      }
    },
    calculateFiatAmount() {
      // Crypto-to-Fiat: takeAmount is what user sends. giveAmount is what user receives.
      if (this.selectedCrypto?.price && this.takeAmount > 0) {
        const fiatValue = this.takeAmount * this.selectedCrypto.price;
        this.giveAmount = fiatValue * (1 - this.feePercentage);
      } else {
        this.giveAmount = 0;
      }
    },
    recalculateAmounts() {
      if (this.fiatToCrypto) {
        this.calculateCryptoAmount();
      } else {
        this.calculateFiatAmount();
      }
    },
    toggleExchangeDirection() {
      this.fiatToCrypto = !this.fiatToCrypto;
      this.$nextTick(() => {
        this.recalculateAmounts();
      });
    },
    handleBuyCrypto() {
      if (this.selectedCrypto && this.isValidAmount) {
        this.$parent.toExchange(
          this.giveAmount,
          this.takeAmount,
          this.selectedCrypto
        );
      }
    },
    toggleAnswer(index) {
      const panelIndex = this.activePanels.indexOf(index);
      if (panelIndex > -1) {
        this.activePanels.splice(panelIndex, 1);
      } else {
        this.activePanels.push(index);
      }
    },
    isActiveAnswer(index) {
      return this.activePanels.includes(index);
    },
    initializeTradingViewWidget() {
      const script = document.createElement("script");
      script.src = "https://s3.tradingview.com/tv.js";
      script.async = true;
      script.onload = () => {
        if (window.TradingView) {
          new window.TradingView.widget({
            autosize: true,
            symbol: "BITSTAMP:BTCUSD",
            interval: "D",
            timezone: "Etc/UTC",
            theme: "light",
            style: "2",
            locale: "en",
            gridColor: "#fff",
            toolbar_bg: "#006E79",
            enable_publishing: false,
            allow_symbol_change: true,
            container_id: "tradingview-widget",
          });
        }
      };
      document.body.appendChild(script);
    },
    getImageUrl(img) {
      return require(`../assets/${img}`);
    },
  },
};
</script>

<style lang="scss" scoped>
.calculation-breakdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 16px 0;

  .total--to-pay {
    padding-top: 12px !important;
    border-top: 1px solid #e2e8f0;

    .desc {
      font-weight: 700;
    }
  }
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.currency-select {
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;

  &:focus {
    outline: none;
    border-color: #006e79;
  }
}
</style>
